import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { Row, Col, Card, CardBody, Button, Spinner, Alert } from "reactstrap"
import { RootState } from "store"
import { isValidOTP } from "utils/helpers"

interface VerifyOTPProps {
    username: string,
    submitButtonText: string,
    resendOTP: () => Promise<boolean>,
    changeEmail: () => void,
    handleVerification: (otp: string) => void
}

const VerifyOTP: React.FC<VerifyOTPProps> = (
    {
        username,
        submitButtonText,
        resendOTP,
        changeEmail,
        handleVerification
    }) => {

    const { loading, error } = useSelector((state: RootState) => state.auth)
    const [resendIn, setResendIn] = useState<number>(0);
    const [resending, setResending] = useState<boolean>(false);

    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']); // 6-digit OTP


    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (resendIn > 0) {
            interval = setInterval(() => {
                setResendIn((prev) => prev - 1);
            }, 1000)
        } else {
            if (interval) {
                clearInterval(interval);
            }
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [resendIn])

    useEffect(() => {
        setResendIn(30);
    }, [])

    const handleResendOTP = async () => {
        if (resendIn || resending) return;
        try {
            setResending(true);
            const success = await resendOTP();
            if (success) {
                setResendIn(30);
                toast.success("OTP has been resent!");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setResending(false);
        }
    }


    const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const previousInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
            previousInput?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('Text').trim();
        if (pastedData.length === otp.length && /^[0-9]+$/.test(pastedData)) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);

            // Focus the last input field
            const lastInput = document.getElementById(`otp-${otp.length - 1}`) as HTMLInputElement;
            lastInput?.focus();
        }
        e.preventDefault(); // Prevent the default paste behavior
    };


    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        const newOtp = [...otp];
        newOtp[index] = value;

        // Move focus to next input when a digit is entered
        if (value && index < otp.length - 1) {
            const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
            nextInput?.focus();
        }

        setOtp(newOtp);
    };


    useEffect(() => {
        if (isValidOTP(otp))
            handleVerification(otp.join(''));
    }, [otp])


    return (
        <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                    <CardBody className="p-4">
                        <div className="mb-4">
                            <div className="avatar-lg mx-auto">
                                <div className="avatar-title bg-light text-primary display-5 rounded-circle">
                                    <i className="ri-mail-line"></i>
                                </div>
                            </div>
                        </div>

                        <div className="p-2 mt-4">
                            <div className="text-muted text-center mb-4 mx-lg-3">
                                <h4 className="">Verify Your Email</h4>
                                <p>Please enter the 6 digit code sent to <span className="fw-semibold">{username} <span onClick={changeEmail} className='text-primary cursor-pointer'><i className='bx bx-edit fs-14'></i></span></span></p>
                            </div>
                            {error && error ? (<Alert color="danger"> {error} </Alert>) : null}
                            <form>
                                <Row>
                                    {
                                        otp.map((digit, index) => {
                                            return (
                                                <Col className="col-2 p-1" key={index}>
                                                    <div className="mb-3">
                                                        <label htmlFor="digit1-input" className="visually-hidden">Digit {index + 1}</label>
                                                        <input
                                                            className="form-control bg-light border-light text-center"
                                                            autoComplete='off'
                                                            key={index}
                                                            id={`otp-${index}`}
                                                            type="tel"
                                                            maxLength={1}
                                                            value={digit}
                                                            onChange={(e) => handleOtpChange(e, index)}
                                                            onKeyDown={(e) => handleBackspace(e, index)}
                                                            onPaste={index === 0 ? handlePaste : undefined} // Only listen to paste on the first input
                                                        />
                                                    </div>
                                                </Col>
                                            )
                                        })
                                    }
                                </Row>
                            </form>
                            <div className="mt-3">
                                <Button onClick={() => {
                                    if (isValidOTP(otp))
                                        handleVerification(otp.join(''))
                                }}
                                    disabled={loading} className="primary-gradient w-100">
                                    {loading && <Spinner size="sm" className='me-2'> Siging in... </Spinner>}
                                    {submitButtonText}
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
                <div className="mt-4 text-center">
                    <p className="mb-0">Didn't receive a code ? <span className={`${(resendIn || resending) ? 'text-secondary' : 'text-primary cursor-pointer'}`} style={{ padding: '2px !important' }} onClick={handleResendOTP}>{resendIn ? `Resend in ${resendIn} seconds` : `${resendIn ? 'Sending...' : 'Resend OTP'}`}</span> </p>
                </div>
            </Col>
        </Row>
    )
}

export default VerifyOTP